import { Box } from '@mui/material';
import { RefreshCw, Search } from 'lucide-react';
import { useCallback, useState, type ReactElement, type SyntheticEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { useRefreshOnFocus } from '@/admin/useRefreshOnFocus';
import {
  AccessDenied,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  SelectField,
  Tabs,
  useDetailRoute,
} from '@/ui/kit';

import {
  ACCOUNT_VIEWS,
  ANY,
  BABIES_FILTERS,
  EMPTY_QUERY,
  ROLE_FILTERS,
  hasActiveCriteria,
  invertedDays,
  isCompleteDay,
  parseAccountQuery,
  serializeAccountQuery,
  type AccountListQuery,
  type AccountView,
} from './accountFilters';
import { accountColumns, AccountsPager } from './AccountsTable';
import { ACCOUNTS_USERS_PATH } from './routes';
import { ACCOUNTS_PER_PAGE, useAccountsList } from './useAccountsList';

/**
 * Liste des comptes (P04, BO-15) : « une personne m'ecrit, qui est-elle ? ».
 *
 * Cinq regles, toutes contraintes par le dossier :
 *
 * 1. **Chaque filtre est execute par le serveur** et le total affiche est le
 *    sien (`COUNT(*)`, filtres compris). Rien n'est compte ni trie cote client :
 *    trier ou filtrer une page serait un mensonge visuel (ADR 0024).
 * 2. **Aucun compteur d'onglet.** Les vues sont des filtres nommes, ecrits ici ;
 *    le serveur ne rend pas de total par vue, et un nombre calcule sur la page
 *    lue serait faux (ADR 0035, point 5).
 * 3. **La recherche est un geste explicite** (Entree ou bouton) : chaque requete
 *    d'administration ecrit une ligne `admin.access` (ADR 0032), une frappe par
 *    lettre noierait le registre. Les vues, filtres et pages sont des gestes
 *    discrets : un appel chacun.
 * 4. **L'etat de la liste vit dans l'adresse** : revenir d'une fiche retrouve la
 *    vue, les filtres et la page.
 * 5. **Un role insuffisant (403) n'est pas une panne** : l'ecran l'explique, il
 *    ne deconnecte personne. Pas d'actualisation automatique (ADR 0035, point 4) :
 *    « Actualiser » et la relecture au retour sur l'onglet.
 */

/**
 * Champ de jour a **brouillon local**.
 *
 * Un champ de date saisi au clavier emet une valeur a chaque segment (annee « 0002 »,
 * « 0020 »… avant « 2026 »). Relie directement a l'adresse, il rejetait ces valeurs
 * partielles et se remettait a vide sous les doigts : la saisie au clavier devenait
 * impossible. Le brouillon garde ce qui est tape ; la valeur n'est ecrite dans
 * l'adresse — donc envoyee au serveur — que **complete**, ou effacee. A la perte du
 * focus, un brouillon incomplet est abandonne au profit de la valeur appliquee.
 */
const DayField = ({
  label,
  value,
  error,
  onCommit,
}: {
  label: string;
  value: string;
  error?: string | undefined;
  onCommit: (value: string) => void;
}): ReactElement => {
  const [draft, setDraft] = useState(value);
  const [applied, setApplied] = useState(value);

  // La valeur appliquee a change ailleurs (« Effacer les filtres », retour arriere) : le brouillon la suit.
  if (value !== applied) {
    setApplied(value);
    setDraft(value);
  }

  const settle = (next: string): void => {
    if (next !== value && (next === '' || isCompleteDay(next))) {
      onCommit(next);
    }
  };

  return (
    <Field
      label={label}
      type="date"
      value={draft}
      error={error}
      onChange={(next) => {
        setDraft(next);
        settle(next);
      }}
      onBlur={() => {
        if (draft === '' || isCompleteDay(draft)) {
          settle(draft);
        } else {
          setDraft(value);
        }
      }}
    />
  );
};

/** Barre de recherche : son brouillon est local, et la cle la remet a zero quand l'adresse change. */
const SearchBar = ({
  initial,
  onSearch,
}: {
  initial: string;
  onSearch: (search: string) => void;
}): ReactElement => {
  const [draft, setDraft] = useState(initial);

  // `SyntheticEvent` et non `FormEvent` : ce dernier est deprecie par React 19.
  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    onSearch(draft.trim());
  };

  return (
    <Box
      component="form"
      role="search"
      aria-label="Recherche de comptes"
      onSubmit={submit}
      sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}
    >
      <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
        <Field
          label="Adresse e-mail"
          icon={Search}
          type="search"
          value={draft}
          onChange={setDraft}
          placeholder="Un fragment suffit, la casse n’a pas d’importance"
        />
      </Box>
      <Button type="submit" variant="primary">
        Rechercher
      </Button>
    </Box>
  );
};

const emptyTitle = (query: AccountListQuery): string =>
  hasActiveCriteria(query) ? 'Aucun compte ne correspond à ces critères' : 'Aucun compte n’est enregistré';

export const AccountsListScreen = (): ReactElement => {
  // Ouvrir une fiche garde la chaine de requete : les filtres de la liste (dans l'URL) survivent.
  const detail = useDetailRoute(ACCOUNTS_USERS_PATH);
  const { search } = useLocation();
  const [params, setParams] = useSearchParams();
  const query = parseAccountQuery(params);
  const [token, setToken] = useState(0);

  const refresh = useCallback(() => {
    setToken((previous) => previous + 1);
  }, []);

  useRefreshOnFocus(refresh);

  const days = invertedDays(query);
  const { data, failure, busy } = useAccountsList(query, { enabled: !days, token });

  // Un geste de recherche repart toujours de la premiere page.
  const change = (patch: Partial<AccountListQuery>): void => {
    setParams(serializeAccountQuery({ ...query, page: 1, ...patch }));
  };

  const items = data?.page.items ?? [];
  const total = data?.page.total ?? -1;
  const totalKnown = total >= 0;
  const outOfRange = data !== null && items.length === 0 && total > 0 && query.page > 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        eyebrow="Utilisateurs"
        title="Comptes"
        subtitle={
          <>
            {totalKnown ? `${total.toLocaleString('fr-FR')} compte${total > 1 ? 's' : ''} · ` : ''}
            Ouvrez une fiche pour voir les profils bébé, les appareils et agir en traçant le motif.
          </>
        }
        actions={
          <Button icon={RefreshCw} onClick={refresh} disabled={busy}>
            {busy ? 'Actualisation…' : 'Actualiser'}
          </Button>
        }
      />

      {failure?.forbidden === true ? (
        <AccessDenied title="Votre compte n’a pas accès à l’administration">
          Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration : il ne peut donc pas consulter les
          comptes des autres personnes. Un rôle s’attribue depuis le serveur, jamais depuis cet écran.
        </AccessDenied>
      ) : (
        <>
          <Tabs
            label="Vues des comptes"
            value={query.view}
            onChange={(view: AccountView) => {
              change({ view });
            }}
            items={ACCOUNT_VIEWS}
            panelId="accounts-panel"
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <SearchBar key={query.search} initial={query.search} onSearch={(search) => { change({ search }); }} />
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
              {/* `ANY` et non `''` : une liste MUI dont la valeur est vide n'affiche aucun libelle. */}
              <SelectField
                label="Rôle"
                value={query.role === '' ? ANY : query.role}
                options={ROLE_FILTERS}
                onChange={(role) => { change({ role: role === ANY ? '' : role }); }}
              />
              <SelectField
                label="Profils bébé"
                value={query.babies === '' ? ANY : query.babies}
                options={BABIES_FILTERS}
                onChange={(babies) => { change({ babies: babies === ANY ? '' : babies }); }}
              />
              <DayField
                label="Inscrit à partir du"
                value={query.createdFrom}
                onCommit={(createdFrom) => { change({ createdFrom }); }}
                error={days ? 'Cette date suit la date de fin.' : undefined}
              />
              <DayField
                label="Inscrit jusqu’au"
                value={query.createdTo}
                onCommit={(createdTo) => { change({ createdTo }); }}
              />
            </Box>
            {hasActiveCriteria(query) ? (
              <Box>
                <Button
                  size="S"
                  onClick={() => {
                    setParams(serializeAccountQuery(EMPTY_QUERY));
                  }}
                >
                  Effacer les filtres
                </Button>
              </Box>
            ) : null}
          </Box>

          {failure === null ? null : (
            <ErrorState message={failure.message} onRetry={refresh} />
          )}

          <Card>
            <Box id="accounts-panel" role="tabpanel" aria-label={`Comptes : ${ACCOUNT_VIEWS.find((view) => view.value === query.view)?.label ?? ''}`}>
              {days ? (
                <EmptyState
                  title="Cette date suit la date de fin"
                  description="Corrigez les dates d’inscription : aucune requête n’est envoyée tant qu’elles sont inversées."
                />
              ) : null}

              {!days && data === null && busy ? <LoadingState label="Chargement des comptes…" /> : null}

              {!days && data !== null && items.length > 0 ? (
                <DataTable
                  label="Comptes"
                  columns={accountColumns(data.loadedAt, search, detail.selectedId !== null)}
                  rows={items}
                  getRowId={(account) => account.id}
                  selectedRowId={detail.selectedId}
                  onRowSelect={(account) => {
                    detail.open(account.id);
                  }}
                />
              ) : null}

              {!days && data !== null && items.length === 0 && !outOfRange ? (
                <EmptyState
                  title={emptyTitle(query)}
                  description={hasActiveCriteria(query) ? 'Élargissez la vue ou retirez un filtre.' : undefined}
                  action={
                    hasActiveCriteria(query) ? (
                      <Button
                        onClick={() => {
                          setParams(serializeAccountQuery(EMPTY_QUERY));
                        }}
                      >
                        Effacer les filtres
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}

              {!days && outOfRange ? (
                <EmptyState
                  title="Cette page n’existe plus"
                  description={`Il n’y a que ${String(Math.ceil(total / ACCOUNTS_PER_PAGE))} page(s) pour ces critères.`}
                  action={
                    <Button
                      onClick={() => {
                        change({ page: 1 });
                      }}
                    >
                      Revenir à la première page
                    </Button>
                  }
                />
              ) : null}
            </Box>
          </Card>

          {data !== null && totalKnown && items.length > 0 ? (
            <AccountsPager
              state={{ page: data.page.page, perPage: data.page.perPage, itemCount: items.length, total }}
              busy={busy}
              onPage={(page) => {
                change({ page });
              }}
            />
          ) : null}
        </>
      )}
    </Box>
  );
};
